import { faCalendar } from "@fortawesome/pro-regular-svg-icons";

import { DropDownElement } from "src/base-components/DropDown";
import {
  FixedPositionedDropdownElement,
  Section,
} from "src/base-components/FixedPositionedDropDown";
import { FixedSimpleDropdown } from "src/base-components/FixedSimpleDropdown";
import { Icon } from "src/base-components/Icon";
import {
  FeatureResponse,
  PropertyDefinitionOutputTypeEnum,
} from "src/clients/features-control";
import { Tooltip } from "src/design-system/Tooltip";
import { TypeIcon } from "src/entities/entityView/EntityDetailsSidePane";
import { getOrderedProperties } from "src/entities/entityView/utils";
import { EntityFieldType, EntitySchemaResource } from "src/entities/queries";
import { formatTimeWindow } from "src/featureCatalogue/utils";

export const sections: Section[] = [
  {
    key: "properties",
    displayText: "Entity properties",
  },
  {
    key: "metadata",
    displayText: "Entity metadata",
  },
  {
    key: "features",
    displayText: "Entity features",
  },
];

const renderValue = (value: string, icon: React.ReactNode) => (
  <div className="flex w-full items-center gap-x-1 overflow-hidden">
    {icon}
    <span className="truncate">{value}</span>
  </div>
);

export type EntityFieldOption = FixedPositionedDropdownElement<
  React.ReactNode,
  string,
  Section[]
> & { fieldType: EntityFieldType };

const metadataOptions: EntityFieldOption[] = [
  {
    key: "_updated_at",
    value: renderValue(
      "Updated at",
      <Icon color="text-gray-500" icon={faCalendar} size="2xs" />,
    ),
    sectionKey: "metadata",
    fieldType: PropertyDefinitionOutputTypeEnum.DATETIME,
  },
];

export const buildEntityFieldOptions = (
  entitySchema: EntitySchemaResource,
  features: FeatureResponse[],
): EntityFieldOption[] => {
  const orderedProperties = getOrderedProperties(entitySchema);

  const entityPropertyOptions = orderedProperties.map((key) => ({
    key: key,
    sectionKey: "properties",
    value: renderValue(
      entitySchema.properties[key]._display_name,
      <TypeIcon
        cardinality={undefined}
        type={entitySchema.properties[key]._type}
      />,
    ),
    fieldType: entitySchema.properties[key]._type,
  }));

  const entityFeatureOptions = features.map((feature) => ({
    key: feature.key,
    sectionKey: "features",
    helpText: feature.name,
    helpBody: (
      <div className="flex flex-col gap-y-2 font-inter-normal-12px text-gray-400">
        <span>{feature.description ?? "<no description>"}</span>
        <div>
          <span className="inline-block w-20">Time period:</span>
          <span className="font-inter-medium-12px text-white">
            {formatTimeWindow(feature.query?.window_length ?? 0)}
          </span>
        </div>
        <div>
          <span className="inline-block w-20">Freshness:</span>
          <span className="font-inter-medium-12px text-white">
            {formatTimeWindow(feature.query?.window_hop ?? 0)}
          </span>
        </div>
      </div>
    ),
    value: renderValue(
      feature.name,
      <TypeIcon
        cardinality={undefined}
        type={feature.feature_type as unknown as EntityFieldType}
      />,
    ),
    fieldType: feature.feature_type as unknown as EntityFieldType,
  }));

  return [
    ...entityPropertyOptions,
    ...metadataOptions,
    ...entityFeatureOptions,
  ];
};

export const EntityFieldsDropdown = ({
  options,
  value,
  onChange,
  error,
  dataLoc,
}: {
  options: EntityFieldOption[];
  value: string;
  onChange: (value: string) => void;
  error: string | undefined;
  dataLoc: string;
}) => {
  return (
    <Tooltip disabled={!error} title={error} asChild>
      <div>
        <FixedSimpleDropdown
          buttonClassName={error ? "border-red-400" : ""}
          buttonDataLoc={`${dataLoc}-button`}
          dataLoc={dataLoc}
          elements={options as DropDownElement<string>[]}
          placeholder="Select field"
          sections={sections}
          selectedKey={value}
          sectionDividers
          onSelect={onChange}
        />
      </div>
    </Tooltip>
  );
};
